import { DailyMetricResponse, JobMetricResponse } from "protos/portal/metrics";
import { DataGridPremiumProps } from "@mui/x-data-grid-premium";
import { ParseKeys } from "i18next";
import { ReportInstanceResponse, ReportResponse } from "protos/portal/reports";

/**
 * A `ProcessedMetrics` is a `DailyMetricResponse` where each value has been replaced
 * with the result of calling the related certified metrics `getValue`.
 * Since this changes the property values, we must do this exactly once.
 */
export const PROCESSED_METRICS_BRAND = Symbol("processedMetrics");

export type ProcessedMetrics = DailyMetricResponse & {
  [PROCESSED_METRICS_BRAND]: true;
};

export interface Aggregates {
  totals: Partial<ProcessedMetrics>;
  averages: Partial<ProcessedMetrics>;
  unanimous: Partial<ProcessedMetrics>;
}

// Our table is like a SQL query with `GROUP BY ROLLUP(serial, date_or_job)` in
// the general case, so we have four kinds of rows:
export enum RowType {
  // GROUP BY (serial, date): Data for one robot on one day.
  SINGLE_DAY = "singleDay",
  // GROUP BY (serial, job): Data for one robot for one job.
  SINGLE_JOB = "singleJob",
  // GROUP BY (serial): Data for one robot, aggregated over all days/jobs.
  ROBOT_AGGREGATES = "robotAggregates",
  // GROUP BY (): Bottom-line data, aggregated over all robots and days/jobs.
  BOTTOM_LINE_AGGREGATES = "bottomLineAggregates",
}

export interface SingleDayRow {
  type: RowType.SINGLE_DAY;
  serial: string;
  customerSerial: string;
  date: string;
  metrics: ProcessedMetrics;
}

export interface SingleJobRow {
  type: RowType.SINGLE_JOB;
  serial: string;
  customerSerial: string;
  jobId: string;
  jobName: string;
  jobStartTimestampMs: number;
  jobStopTimestampMs: number;
  metrics: ProcessedMetrics;
}

export interface RobotAggregatesRow {
  type: RowType.ROBOT_AGGREGATES;
  serial: string;
  customerSerial: string;
  aggregates: Aggregates;
}

export interface BottomLineAggregatesRow {
  type: RowType.BOTTOM_LINE_AGGREGATES;
  aggregationType: AggregationType & ParseKeys; // split into separate rows
  aggregates: Aggregates;
}

export type DataRow =
  | SingleJobRow
  | SingleDayRow
  | RobotAggregatesRow
  | BottomLineAggregatesRow;

export enum AggregationType {
  TOTAL = /* i18nKey: */ "views.reports.scheduled.table.fields.total",
  AVERAGE = /* i18nKey: */ "views.reports.scheduled.table.fields.average",
}

export interface ReportTableBase {
  loading?: boolean;
  onColumnOrderChange?: DataGridPremiumProps["onColumnOrderChange"];
  // Set for a saved run, which has no customer of its own. The report page
  // reads this from the report instead.
  customerUuid?: string;
}

export interface ReportTableReport extends ReportTableBase {
  report?: ReportResponse;
}

export interface ReportTableInstance extends ReportTableBase {
  instance: ReportInstanceResponse | undefined;
}

export type ReportTableProps = ReportTableInstance | ReportTableReport;

export const isReport = (input: ReportTableProps): input is ReportTableReport =>
  "report" in input;

export const isInstance = (
  input: ReportTableProps
): input is ReportTableInstance => "instance" in input;

export interface CertifiedMetricsParams {
  serials: string[];
  startDate: string;
  endDate: string;
  fields: string[];
  useJigsaw: boolean;
}

export type CertifiedMetricRow = DailyMetricResponse | JobMetricResponse;
